/**
 * @file Dungeon population debug window (issue #44). Toggled with `-`.
 *
 * A balancing sandbox for the dungeon generator's enemy pre-population and
 * per-floor progression. It shows what the *current* floor was built with (room,
 * enemy and prop counts read from {@link ../world/dungeon/DungeonArena.js}), lets
 * the {@link ../config/GameConfig.js CONFIG.dungeon.population} knobs be edited
 * live, and renders the {@link ../config/DungeonProps.js} catalog so the
 * collider policy of every prop is visible at a glance.
 *
 * Sibling of {@link ./WaveDebugPanel.js}: same lifecycle (built on first open,
 * inert while closed, DOM rather than in-scene). Where the wave panel edits the
 * frozen `WAVES` through a persisted override map, this one edits
 * `CONFIG.dungeon.population`/`progression` directly — those sub-objects are left
 * mutable on purpose, and the *next* floor build reads the new values, so every
 * edit here is annotated "applies next floor".
 */

import { DUNGEON_PROPS } from '../config/DungeonProps.js';

/** Seconds between refreshes while open. The panel is a readout, not a HUD. */
const REFRESH_INTERVAL = 0.25;

/** Coexistence modes, in the order the `mode` button cycles them. */
const MODES = Object.freeze(['prepopulated', 'waves', 'both']);

export class DungeonDebugPanel {
  /**
   * @param {HTMLElement} root Container element; the panel appends itself to it.
   */
  constructor(root) {
    /** @type {HTMLElement} @private */
    this._root = root;
    /** @type {HTMLElement | null} Built on first open. @private */
    this._el = null;
    /** @type {Record<string, HTMLElement>} @private */
    this._parts = {};
    /**
     * Last context seen by {@link update}. Delegated event handlers read the
     * live config through it, so they must not fire before the first tick — the
     * panel only reveals editable rows once it has a context.
     * @type {import('../core/Context.js').GameContext | null}
     * @private
     */
    this._ctx = null;
    /** @type {boolean} @private */
    this._open = false;
    /** @type {number} @private */
    this._sinceRefresh = 0;
  }

  /** @returns {boolean} True while the window is on screen. */
  get isOpen() {
    return this._open;
  }

  /**
   * Shows or hides the window.
   * @returns {void}
   */
  toggle() {
    this._open ? this.close() : this.open();
  }

  /** @returns {void} */
  open() {
    if (!this._el) this._build();
    this._open = true;
    this._el.hidden = false;
    this._sinceRefresh = REFRESH_INTERVAL;
  }

  /** @returns {void} */
  close() {
    this._open = false;
    if (this._el) this._el.hidden = true;
  }

  /**
   * Per-frame tick. Returns immediately while closed, so a shipped-but-unused
   * panel costs one boolean test per frame.
   *
   * @param {number} dt Delta time in seconds.
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   */
  update(dt, ctx) {
    if (!this._open) return;
    this._ctx = ctx;

    this._sinceRefresh += dt;
    if (this._sinceRefresh < REFRESH_INTERVAL) return;
    this._sinceRefresh = 0;
    this._refresh(ctx);
  }

  /** @returns {void} @private */
  _build() {
    const panel = document.createElement('div');
    panel.className = 'dungeon-debug';
    panel.hidden = true;
    panel.innerHTML = `
      <header class="dungeon-debug__head">
        <span class="dungeon-debug__title">Dungeon sandbox</span>
        <span class="dungeon-debug__key"><kbd>−</kbd></span>
        <button type="button" class="dungeon-debug__close" data-dd="close" title="Close">×</button>
      </header>

      <div class="dungeon-debug__body">
        <section class="dungeon-debug__section">
          <h4 class="dungeon-debug__heading">Population <span class="dungeon-debug__hint">applies next floor</span></h4>
          <div class="dungeon-debug__grid">
            <label class="dungeon-debug__field dungeon-debug__field--wide">
              <span>Enabled</span>
              <input type="checkbox" data-toggle="population" />
            </label>
            <label class="dungeon-debug__field dungeon-debug__field--wide">
              <span>Mode</span>
              <button type="button" class="dungeon-debug__mode" data-dd="mode">—</button>
            </label>
            <label class="dungeon-debug__field">
              <span>Min enemies</span>
              <input type="number" data-pop="minEnemies" step="1" min="0" />
            </label>
            <label class="dungeon-debug__field">
              <span>Max enemies</span>
              <input type="number" data-pop="maxEnemies" step="1" min="0" />
            </label>
            <label class="dungeon-debug__field">
              <span>Safe radius</span>
              <input type="number" data-pop="safeRadiusFromEntrance" step="1" min="0" />
            </label>
            <label class="dungeon-debug__field">
              <span>Max pre-placed</span>
              <input type="number" data-pop="maxPrePlaced" step="1" min="0" />
            </label>
          </div>
          <p class="dungeon-debug__note" data-dd="perRoom">—</p>
        </section>

        <section class="dungeon-debug__section">
          <h4 class="dungeon-debug__heading">Progression <span class="dungeon-debug__hint">applies next floor</span></h4>
          <label class="dungeon-debug__field dungeon-debug__field--wide">
            <span>Enabled</span>
            <input type="checkbox" data-toggle="progression" />
          </label>
          <p class="dungeon-debug__note" data-dd="curve">—</p>
        </section>

        <section class="dungeon-debug__section">
          <h4 class="dungeon-debug__heading">Live dungeon</h4>
          <div class="dungeon-debug__stats">
            <div><span>Rooms</span><b data-dd="rooms">—</b></div>
            <div><span>Enemies</span><b data-dd="enemies">—</b></div>
            <div><span>Target</span><b data-dd="enemyTarget">—</b></div>
            <div><span>Solid props</span><b data-dd="solidProps">—</b></div>
            <div><span>Pre-placed</span><b data-dd="prePlaced">—</b></div>
          </div>
          <p class="dungeon-debug__note" data-dd="formations">—</p>
        </section>

        <section class="dungeon-debug__section">
          <h4 class="dungeon-debug__heading">Prop catalog</h4>
          <table class="dungeon-debug__table">
            <thead>
              <tr><th>Kind</th><th>Placement</th><th>Room types</th><th>Collider</th></tr>
            </thead>
            <tbody data-dd="props"></tbody>
          </table>
        </section>
      </div>
    `;

    this._root.appendChild(panel);
    this._el = panel;

    for (const node of panel.querySelectorAll('[data-dd]')) {
      this._parts[/** @type {HTMLElement} */ (node).dataset.dd] = /** @type {HTMLElement} */ (node);
    }

    // The prop catalog is static data: build it once and never touch it again.
    this._parts.props.innerHTML = Object.values(DUNGEON_PROPS)
      .map((row) => this._propRowHtml(row))
      .join('');

    // One delegated listener each, rather than one per control: the population
    // rows are stable, but delegation keeps wiring in a single place.
    panel.addEventListener('input', (event) => this._onInput(event));
    panel.addEventListener('change', (event) => this._onChange(event));
    panel.addEventListener('click', (event) => this._onClick(event));

    // Enter commits a number edit and hands the keyboard back to the game.
    panel.addEventListener('keydown', (event) => {
      if (/** @type {KeyboardEvent} */ (event).key === 'Enter') {
        /** @type {HTMLElement} */ (event.target).blur?.();
      }
    });
  }

  /**
   * Number-field edits: parse to a non-negative integer and write it back to the
   * live population config, keeping `minEnemies <= maxEnemies`.
   * @param {Event} event Delegated `input` event.
   * @returns {void}
   * @private
   */
  _onInput(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const field = input.dataset?.pop;
    const population = this._ctx?.config?.dungeon?.population;
    if (!field || !population) return;

    if (input.value.trim() === '') return; // Leave a mid-edit blank alone.
    let value = Math.max(0, Math.round(Number(input.value)));
    if (!Number.isFinite(value)) return;

    population[field] = value;
    // Keep the range coherent regardless of which end the user dragged.
    if (field === 'minEnemies' && value > population.maxEnemies) population.maxEnemies = value;
    if (field === 'maxEnemies' && value < population.minEnemies) population.minEnemies = value;
  }

  /**
   * Checkbox edits: flip the matching `enabled` flag.
   * @param {Event} event Delegated `change` event.
   * @returns {void}
   * @private
   */
  _onChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const block = input.dataset?.toggle;
    const target = block && this._ctx?.config?.dungeon?.[block];
    if (!target) return;
    target.enabled = input.checked;
  }

  /**
   * Button clicks: close, or cycle the coexistence mode.
   * @param {Event} event Delegated `click` event.
   * @returns {void}
   * @private
   */
  _onClick(event) {
    const action = /** @type {HTMLElement} */ (event.target).dataset?.dd;
    if (action === 'close') {
      this.close();
      return;
    }
    if (action === 'mode') {
      const population = this._ctx?.config?.dungeon?.population;
      if (!population) return;
      const next = (MODES.indexOf(population.mode) + 1) % MODES.length;
      population.mode = MODES[next];
      this._parts.mode.textContent = population.mode;
    }
  }

  /**
   * @param {import('../config/DungeonProps.js').PropRow} row Catalog row.
   * @returns {string} One table row of HTML.
   * @private
   */
  _propRowHtml(row) {
    const rooms = Array.isArray(row.roomTypes) ? row.roomTypes.join(',') : '*';
    const c = row.collider;
    const collider = c ? `${c.halfX}×${c.halfZ}${c.blocksProjectiles ? ' ⛌bullets' : ''}` : '—';
    return `
      <tr data-prop="${row.kind}">
        <th scope="row">${row.kind}</th>
        <td>${row.placement}</td>
        <td>${rooms}</td>
        <td data-cell="collider">${collider}</td>
      </tr>`;
  }

  /**
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   * @private
   */
  _refresh(ctx) {
    const population = ctx?.config?.dungeon?.population;
    const progression = ctx?.config?.dungeon?.progression;

    if (population) {
      this._setCheckbox('population', population.enabled);
      this._parts.mode.textContent = population.mode;
      this._setNumber('minEnemies', population.minEnemies);
      this._setNumber('maxEnemies', population.maxEnemies);
      this._setNumber('safeRadiusFromEntrance', population.safeRadiusFromEntrance);
      this._setNumber('maxPrePlaced', population.maxPrePlaced);
      this._parts.perRoom.textContent = this._perRoomText(population.perRoom);
    }

    if (progression) {
      this._setCheckbox('progression', progression.enabled);
      this._parts.curve.textContent = this._curveText(progression);
    }

    this._refreshLive(ctx);
  }

  /**
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   * @private
   */
  _refreshLive(ctx) {
    const stats = ctx?.arena?.dungeon?.stats;
    const dash = () => {
      for (const key of ['rooms', 'enemies', 'enemyTarget', 'solidProps', 'prePlaced']) {
        this._parts[key].textContent = '—';
      }
      this._parts.formations.textContent = '—';
    };

    if (!stats) {
      dash();
      return;
    }

    this._parts.rooms.textContent = String(stats.rooms ?? '—');
    this._parts.enemies.textContent = String(stats.enemies ?? '—');
    this._parts.enemyTarget.textContent = String(stats.enemyTarget ?? '—');
    this._parts.solidProps.textContent = String(stats.solidProps ?? '—');
    this._parts.prePlaced.textContent = String(ctx.arena?.prePlacedCount ?? '—');
    this._parts.formations.textContent = stats.formations?.length
      ? `Formations: ${stats.formations.join(', ')}`
      : 'Formations: —';
  }

  /**
   * @param {string} field Population field name.
   * @param {number} value Current value.
   * @returns {void} Writes the value unless the field is being typed in.
   * @private
   */
  _setNumber(field, value) {
    const input = /** @type {HTMLInputElement} */ (
      this._el.querySelector(`input[data-pop="${field}"]`)
    );
    if (!input || input === document.activeElement) return;
    const text = String(value);
    if (input.value !== text) input.value = text;
  }

  /**
   * @param {string} block `population` or `progression`.
   * @param {boolean} checked Current flag value.
   * @returns {void}
   * @private
   */
  _setCheckbox(block, checked) {
    const input = /** @type {HTMLInputElement} */ (
      this._el.querySelector(`input[data-toggle="${block}"]`)
    );
    if (!input || input === document.activeElement) return;
    if (input.checked !== Boolean(checked)) input.checked = Boolean(checked);
  }

  /**
   * @param {Record<string, { min: number, max: number }>} perRoom Per-room ranges.
   * @returns {string}
   * @private
   */
  _perRoomText(perRoom) {
    if (!perRoom) return 'Per room: —';
    const parts = Object.entries(perRoom).map(([type, r]) => `${type} ${r.min}–${r.max}`);
    return `Per room: ${parts.join(', ')}`;
  }

  /**
   * @param {object} progression Progression curve block.
   * @returns {string}
   * @private
   */
  _curveText(progression) {
    const line = (label, curve) =>
      curve ? `${label} +${curve.perLevel}/lvl (max ${curve.max})` : `${label} —`;
    return [
      line('rooms', progression.roomCount),
      line('loops', progression.loopChance),
      line('decor', progression.decorDensity),
      line('enemies', progression.enemies)
    ].join(' · ');
  }

  /** @returns {void} Removes the window and its listeners from the document. */
  dispose() {
    this._el?.remove();
    this._el = null;
    this._parts = {};
    this._ctx = null;
    this._open = false;
  }
}
